import React, { useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import { toast, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

Modal.setAppElement("#root");

const PriceSurgeModal = ({ isOpen, onRequestClose, onConfirm, editData }) => {
  const startRef = useRef();
  const endRef = useRef();
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [multiplier, setMultiplier] = useState("");
  const options = ["1.5", "2", "2.5", "3", "3.5", "4", "4.5", "5"];

  // Generate hours array (0-23)
  const hours = Array.from({ length: 24 }, (_, i) => i);

  useEffect(() => {
    if (isOpen && editData) {
      // Extract hour from formatted string (e.g., "14:00" -> "14")
      setStartTime(editData.startTime.split(":")[0]);
      setEndTime(editData.endTime.split(":")[0]);
      setMultiplier(editData.multiplier);
    } else if (isOpen) {
      // Reset form when opening for new entry
      setStartTime("");
      setEndTime("");
      setMultiplier("");
    }
    if (startRef?.current) {
      startRef?.current?.focus();
    }
  }, [isOpen, editData]);

  const validateHour = (hour) => {
    const hourNum = parseInt(hour);
    return !isNaN(hourNum) && hourNum >= 0 && hourNum <= 23;
  };

  const compareHours = (hour1, hour2) => {
    const h1 = parseInt(hour1);
    const h2 = parseInt(hour2);
    return h1 - h2;
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="Surge Price"
      className="max-w-lg w-full bg-white rounded-xl shadow-lg p-8 mx-auto relative transform transition-all duration-300 ease-in-out"
      overlayClassName="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center"
    >
      <h2 className="text-2xl font-semibold mb-6 text-dazzledBlue text-center">
        {editData ? "Edit Surge Price" : "Add Surge Price"}
      </h2>

      <div className="grid grid-cols-1 gap-6">
        <div>
          <label className="block text-dazzledBlue font-medium mb-2">
            Start Hour (24-hour format):
          </label>
          <select
            ref={startRef}
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            className="w-full py-3 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dazzledBlue focus:border-transparent transition-all"
          >
            <option value="">Select Start Hour</option>
            {hours.map((hour) => {
              const displayHour =
                hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
              const ampm = hour < 12 ? "AM" : "PM";
              return (
                <option key={hour} value={hour}>
                  {hour.toString().padStart(2, "0")}:00 ({displayHour}:00 {ampm}
                  )
                </option>
              );
            })}
          </select>
          <p className="text-sm text-gray-500 mt-1">Select hour (0-23)</p>
        </div>

        <div>
          <label className="block text-dazzledBlue font-medium mb-2">
            End Hour (24-hour format):
          </label>
          <select
            ref={endRef}
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            className="w-full py-3 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dazzledBlue focus:border-transparent transition-all"
          >
            <option value="">Select End Hour</option>
            {hours.map((hour) => {
              const displayHour =
                hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
              const ampm = hour < 12 ? "AM" : "PM";
              return (
                <option key={hour} value={hour}>
                  {hour.toString().padStart(2, "0")}:00 ({displayHour}:00 {ampm}
                  )
                </option>
              );
            })}
          </select>
          <p className="text-sm text-gray-500 mt-1">Select hour (0-23)</p>
        </div>

        <div>
          <label className="block text-dazzledBlue font-medium mb-2">
            Multiplier:
          </label>
          <select
            className="w-full py-3 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dazzledBlue transition-all text-dazzledBlue"
            value={multiplier}
            onChange={(e) => setMultiplier(e.target.value)}
          >
            <option value="">Select Multiplier</option>
            {options.map((option) => (
              <option key={option} value={option}>
                {option}x
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex justify-end space-x-3 mt-8">
        <button
          onClick={onRequestClose}
          className="bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300"
        >
          Cancel
        </button>
        <button
          onClick={() => {
            if (!startTime || !endTime || !multiplier) {
              toast.error("Please fill all the fields.", {
                position: "top-right",
                autoClose: 5000,
                transition: Bounce,
              });
              return;
            }

            if (!validateHour(startTime)) {
              toast.error("Please select a valid start hour (0-23).", {
                position: "top-right",
                autoClose: 5000,
                transition: Bounce,
              });
              return;
            }

            if (!validateHour(endTime)) {
              toast.error("Please select a valid end hour (0-23).", {
                position: "top-right",
                autoClose: 5000,
                transition: Bounce,
              });
              return;
            }

            if (compareHours(endTime, startTime) < 0) {
              toast.error(
                "End hour must be greater than or equal to start hour.",
                {
                  position: "top-right",
                  autoClose: 5000,
                  transition: Bounce,
                }
              );
              return;
            }

            onConfirm({
              startTime,
              endTime,
              multiplier,
              id: editData ? editData.id : null,
              docId: editData?.docId,
            });
          }}
          className="bg-celadon text-primary py-2 px-6 rounded-lg transition-colors"
        >
          {editData ? "Update" : "Add"}
        </button>
      </div>
    </Modal>
  );
};

export default PriceSurgeModal;

